// Comparisons of two or more models: the overlay and side-by-side diagrams, and the matrix of
// entities across designs. Pure functions over models; the view adds loading and layout.
import { changeEntity, diffModels, rootOfPath, type ModelDiff, type ModelSpec, type RenameSpec } from '@modelviewer/core';
import { sizeNode, type DiffState, type EntityNodeData, type Graph, type GraphOptions, type Line } from '../diagram/graph';
import { buildDiffGraph } from '../diff/diffGraph';

export interface CompareSide {
  model: ModelSpec;
  /** Renames declared by the newer model, when it was branched from the older one. */
  renames?: RenameSpec[];
}

export function compareTwo(a: CompareSide, b: CompareSide): ModelDiff {
  return diffModels(a.model, b.model, { renames: b.renames });
}

/** Renames that turn the newer model's names back into the older ones. */
function invertRenames(diff: ModelDiff, b: ModelSpec): RenameSpec[] {
  const toA = new Map(diff.entities.map((m) => [m.to, m.from]));
  const out: RenameSpec[] = diff.entities.filter((m) => m.how !== 'name').map((m) => ({ from: m.to, to: m.from }));
  for (const c of diff.changes) {
    if (c.kind !== 'property-renamed') continue;
    const { root, rest } = rootOfPath(b, c.entity);
    out.push({ entity: [toA.get(root) ?? root, ...rest].join('.'), from: c.to, to: c.from });
  }
  return out;
}

const lineState = (l: Line) => (l.kind === 'row' ? l.row.diff : l.section.diff);

/** Drops ghosts (removed cards, rows and edges) and optionally recolors what is left. */
function keepOnly(graph: Graph, options: GraphOptions, recolor?: (state: DiffState) => DiffState): Graph {
  const nodes = graph.nodes
    .filter((n) => n.diff !== 'removed')
    .map((n): EntityNodeData => {
      const lines = n.lines
        .filter((l) => lineState(l) !== 'removed')
        .map((l): Line => {
          if (!recolor) return l;
          if (l.kind === 'row') return l.row.diff ? { kind: 'row', row: { ...l.row, diff: recolor(l.row.diff) } } : l;
          return l.section.diff ? { kind: 'section', section: { ...l.section, diff: recolor(l.section.diff) } } : l;
        });
      return sizeNode({ ...n, lines, diff: n.diff && recolor ? recolor(n.diff) : n.diff }, options.measure);
    });
  const ids = new Set(nodes.map((n) => n.id));
  const edges = graph.edges
    .filter((e) => e.diff !== 'removed' && ids.has(e.source) && ids.has(e.target))
    .map((e) => (e.diff && recolor ? { ...e, diff: recolor(e.diff) } : e));
  return { nodes, edges };
}

/**
 * Diagrams for two models, with node ids in the older model's names so they share one layout:
 * `overlay` (the newer model with ghosts of what was removed), `right` (the newer model: new and
 * changed parts marked) and `left` (the older model: parts that are gone or changed marked).
 */
export function compareGraphs(a: CompareSide, b: CompareSide, diff: ModelDiff, options: GraphOptions): { overlay: Graph; left: Graph; right: Graph } {
  const toA = new Map(diff.entities.map((m) => [m.to, m.from]));
  const inA = (k: string) => toA.get(k) ?? k;
  const overlay = buildDiffGraph({ before: a.model, after: b.model, diff, options, idAfter: inA, idBefore: (k) => k });
  const right = keepOnly(overlay, options);
  const back = diffModels(b.model, a.model, { renames: invertRenames(diff, b.model) });
  const inverse = buildDiffGraph({ before: b.model, after: a.model, diff: back, options, idAfter: (k) => k, idBefore: inA });
  const left = keepOnly(inverse, options, (s) => (s === 'added' ? 'removed' : s));
  return { overlay, left, right };
}

// ------------------------------------------------------------------ matrix

export type CellState = 'same' | 'added' | 'changed';

export interface MatrixCell {
  /** The entity's name in this design. */
  key: string;
  state: CellState;
  /** `was Author` for renamed entities. */
  note?: string;
  properties: number;
  changes: number;
}

export interface MatrixRow {
  id: string;
  /** One cell per design; null where the design has no such entity. */
  cells: (MatrixCell | null)[];
}

export interface DesignSummary {
  entities: number;
  relationships: number;
  properties: number;
  /** Logical changes compared with the first design. */
  changes: number;
}

interface Members {
  properties: Record<string, unknown>;
  owned?: Record<string, Members>;
  complex?: Record<string, Members>;
}

const countMembers = (c: Members): number =>
  Object.keys(c.properties).length + Object.values(c.owned ?? {}).reduce((n, o) => n + countMembers(o), 0) + Object.values(c.complex ?? {}).reduce((n, x) => n + countMembers(x), 0);

/** Properties of an entity, including those of its owned and complex types. */
function propertyCount(model: ModelSpec, key: string): number {
  const e = model.entities[key];
  return e ? countMembers(e) : 0;
}

/**
 * Entities across designs, lined up by the first design: matched (possibly renamed) entities share
 * a row, and entities the first design does not have get rows of their own, merged by name.
 */
export function compareMatrix(models: ModelSpec[], diffs: (ModelDiff | null)[]): { rows: MatrixRow[]; summary: DesignSummary[] } {
  const first = models[0]!;
  const rows = new Map<string, MatrixRow>();
  const row = (id: string) => {
    let r = rows.get(id);
    if (!r) {
      r = { id, cells: models.map(() => null) };
      rows.set(id, r);
    }
    return r;
  };
  for (const key of Object.keys(first.entities).sort()) row(key).cells[0] = { key, state: 'same', properties: propertyCount(first, key), changes: 0 };
  const added: string[] = [];
  models.forEach((model, i) => {
    if (i === 0) return;
    const diff = diffs[i];
    if (!diff) return;
    const touched = new Map<string, number>();
    for (const c of diff.changes) {
      const path = c.kind === 'entity-renamed' ? null : changeEntity(c);
      if (!path) continue;
      const root = rootOfPath(model, path).root;
      touched.set(root, (touched.get(root) ?? 0) + 1);
    }
    const matched = new Set<string>();
    for (const m of diff.entities) {
      matched.add(m.to);
      const changes = touched.get(m.to) ?? 0;
      row(m.from).cells[i] = {
        key: m.to,
        state: changes > 0 || m.from !== m.to ? 'changed' : 'same',
        note: m.from !== m.to ? `was ${m.from}` : undefined,
        properties: propertyCount(model, m.to),
        changes,
      };
    }
    for (const key of Object.keys(model.entities)) {
      if (matched.has(key)) continue;
      if (!rows.has(`+${key}`)) added.push(key);
      row(`+${key}`).cells[i] = { key, state: 'added', properties: propertyCount(model, key), changes: 0 };
    }
  });
  const summary = models.map((model, i) => ({
    entities: Object.keys(model.entities).length,
    relationships: model.relationships.length,
    properties: Object.keys(model.entities).reduce((n, k) => n + propertyCount(model, k), 0),
    changes: i === 0 ? 0 : (diffs[i]?.changes.length ?? 0),
  }));
  const ordered = [...Object.keys(first.entities).sort(), ...added.sort().map((k) => `+${k}`)];
  return { rows: ordered.map((id) => rows.get(id)!), summary };
}
